import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PhoneCall, Mail, MapPin, Clock, Send, CheckCircle, Calendar, User, MessageCircle } from 'lucide-react';
import { BRAND } from '../../data/brand';
import { useTranslation } from '../../hooks/useTranslation';
import BookingCalendar from '../ui/BookingCalendar';
import PhoneInput from '../ui/PhoneInput';

export default function Contact() {
  const { t, currentLanguage } = useTranslation();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+49',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          selectedDate,
          selectedTime,
          language: currentLanguage,
          service: 'Kostenlose Beratung'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        console.error('Ошибка при отправке формы:', result.error);
        alert(result.error || 'Произошла ошибка при отправке формы');
      }
    } catch (error) {
      console.error('Ошибка сети:', error);
      alert('Ошибка сети. Пожалуйста, попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
    
    // Сброс формы через 5 секунд
    setTimeout(() => {
      setIsSubmitted(false);
      setShowForm(false);
      setSelectedDate('');
      setSelectedTime('');
      setFormData({
        name: '',
        email: '',
        phone: '',
        countryCode: '+49',
        message: ''
      });
    }, 5000);
  };

  const handleDateTimeSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
    setShowForm(true);
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section id="contact" className="py-16 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Заголовок секции - перемещен наверх */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-slate-800">
            {t('booking.title')}
          </motion.h2>
          <motion.p variants={item} className="text-slate-600 text-base sm:text-lg max-w-3xl mx-auto">
            {t('booking.description')}
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
          
          {/* Левая колонка - календарь и контактная информация */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Календарь */}
            <motion.div variants={item}>
              <BookingCalendar onDateTimeSelect={handleDateTimeSelect} />
            </motion.div>


          </motion.div>

          {/* Правая колонка - форма и информация */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Сообщение когда время не выбрано */}
            {(!selectedDate || !selectedTime) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200 text-center"
              >
                <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-slate-400 mx-auto mb-6" />
                <h3 className="text-lg sm:text-xl font-semibold mb-4 text-slate-800">
                  {t('contact.consultation.title')}
                </h3>
                <p className="text-slate-600 mb-6 text-sm sm:text-base">
                  {t('contact.consultation.description')}
                </p>
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 sm:p-4">
                  <h4 className="font-medium text-rose-800 mb-2 text-sm sm:text-base">{t('contact.expectations.title')}</h4>
                  <ul className="text-xs sm:text-sm text-rose-700 space-y-1 text-left">
                    <li>• {t('contact.expectations.consultation')}</li>
                    <li>• {t('contact.expectations.discussion')}</li>
                    <li>• {t('contact.expectations.recommendations')}</li>
                    <li>• {t('contact.expectations.questions')}</li>
                  </ul>
                </div>
              </motion.div>
            )}

            {/* Выбранная дата и время */}
            {(selectedDate && selectedTime) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-primary-50 border border-primary-200 rounded-2xl p-6"
              >
                <h3 className="text-xl font-semibold text-primary-800 mb-4 flex items-center gap-3">
                  <Calendar className="h-6 w-6" />
                  {t('contact.selected_time')}
                </h3>
                <div className="space-y-2">
                  <p className="text-primary-700">
                    <span className='font-medium'>{t('contact.date')}:</span> {selectedDate}
                  </p>
                  <p className="text-primary-700">
                    <span className='font-medium'>{t('contact.time')}:</span> {selectedTime}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Форма записи */}
            {showForm && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200"
              >
                <h3 className="text-lg sm:text-xl font-semibold mb-6 text-slate-800 flex items-center gap-3">
                  <User className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
                  {t('contact.your_data')}
                </h3>
                
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('contact.form.name')} *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                          placeholder={t('contact.form.name_placeholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('contact.form.email')} *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                          placeholder={t('contact.form.email_placeholder')}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('contact.form.phone')}
                      </label>
                      <PhoneInput 
                        value={formData} 
                        onChange={handleInputChange}
                        placeholder={t('contact.form.phone_placeholder')}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('contact.form.message')}
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                        placeholder={t('contact.form.message_placeholder')}
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-sm sm:text-base"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          {t('contact.form.submitting')}
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                          {t('contact.form.submit')}
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2">
                      {t('contact.form.success_title')}
                    </h3>
                    <p className="text-slate-600 text-sm sm:text-base">
                      {t('contact.form.success_message', { date: selectedDate, time: selectedTime })}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
